import { AuthSession, SessionStorageAdapter } from '@lumora/common';

import { sessionManager } from '../sessionManager';

/**
 * Web session adapter for browser environments
 * Uses localStorage/sessionStorage for session persistence
 */
export class WebSessionAdapter implements SessionStorageAdapter {
  private storageKey: string;
  private rememberMe: boolean;

  constructor(
    storageKey: string = 'lumora_session',
    rememberMe: boolean = false
  ) {
    this.storageKey = storageKey;
    this.rememberMe = rememberMe;
  }

  async storeSession(session: AuthSession): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
      const sessionString = JSON.stringify({
        ...session,
        // Store complete session with tokens for page refresh persistence
      });

      if (this.rememberMe) {
        localStorage.setItem(this.storageKey, sessionString);
      } else {
        sessionStorage.setItem(this.storageKey, sessionString);
      }
    } catch (error) {
      console.error('Failed to persist session:', error);
    }
  }

  async getSession(): Promise<AuthSession | null> {
    if (typeof window === 'undefined') return null;

    // Try to get from session manager first (source of truth)
    const supabaseSession = sessionManager.getSupabaseSession();
    if (supabaseSession) {
      return {
        user: supabaseSession.user,
        token: supabaseSession.access_token,
        expiresAt: supabaseSession.expires_at!,
        refreshToken: supabaseSession.refresh_token,
      } as unknown as AuthSession;
    }

    try {
      const sessionString =
        localStorage.getItem(this.storageKey) ||
        sessionStorage.getItem(this.storageKey);

      if (!sessionString) return null;

      const sessionData = JSON.parse(sessionString) as AuthSession;

      // Validate session data
      if (!sessionData.user || !sessionData.expiresAt) {
        await this.clearSession();
        return null;
      }

      // Check if session is expired
      if (Date.now() > sessionData.expiresAt * 1000) {
        await this.clearSession();
        return null;
      }

      return sessionData;
    } catch (error) {
      console.error('Failed to load persisted session:', error);
      await this.clearSession();
      return null;
    }
  }

  async clearSession(): Promise<void> {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(this.storageKey);
    sessionStorage.removeItem(this.storageKey);
  }

  isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  /**
   * Set remember me preference
   */
  setRememberMe(rememberMe: boolean): void {
    this.rememberMe = rememberMe;
  }
}
